import { useMemo, useState } from "react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { Bar, CartesianGrid, Cell, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { UsersIcon } from "@heroicons/react/24/outline";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/20/solid";
import { cn, formatAmount } from "@utils";
import {
  FOLLOW_UP_DAYS,
  LAYER_COUNT,
  buildBurndown,
  burndownLayers,
  burndownMonthName,
  shortMonthName,
  useOverdueBurndown,
} from "../hooks/overdueBurndown";
import WorkingSetIcon from "./WorkingSetIcon";

// One ramp per state, light → dark with age (index 0 = due this month): pale gray normally, violet on days Ontik
// sent payment requests, and a stronger gray for today and the hovered day so they stand out.
const GRAY = ["#f4f4f5", "#ececee", "#e4e4e7", "#d4d4d8", "#c4c4cc"];
const VIOLET = ["#f5f3ff", "#ede9fe", "#ddd6fe", "#c4b5fd", "#a78bfa"];
const STRONG = ["#dcdce0", "#d0d0d6", "#c2c2c9", "#b2b2bb", "#a1a1aa"];
const MARKER = "#7c3aed";
const LAST_MONTH = "#a1a1aa";
// Payment requests go out most days, so only days with more than this many get a marker (and tooltip details).
const TOUCHPOINT_MARKER_MIN = 10;
const hasTouchpointMarker = (row) => row.touchpoints > TOUCHPOINT_MARKER_MIN;
const hasLastMonthMarker = (row) => row.lastMonth !== null && row.lastMonthTouchpoints > TOUCHPOINT_MARKER_MIN;
const TOOLTIP_OFFSET = 14;
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const compactAmount = (value) =>
  `£${Number(value).toLocaleString("en-GB", { notation: "compact", maximumFractionDigits: 1 }).toLowerCase()}`;

const wholePounds = (value) => `£${Math.round(value).toLocaleString("en-GB")}`;

const percent = (part, whole) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : "—");

const PlaceholderBar = ({ x, y, width, height }) =>
  height > 0 ? (
    <rect x={x + 0.5} y={y + 0.5} width={Math.max(width - 1, 0)} height={Math.max(height - 1, 0)} rx={3}
      fill="#fafafa" stroke="#e4e4e7" strokeDasharray="2 3" />
  ) : null;

const TouchpointMarker = ({ viewBox }) => (
  <path d={`M${viewBox.x - 5},${viewBox.y - 9} h10 l-5,7 z`} fill={MARKER} />
);

const LastMonthTouchpointDot = ({ cx, cy, payload }) =>
  payload && hasLastMonthMarker(payload) && cy != null ? (
    <circle key={payload.day} cx={cx} cy={cy} r={3.5} fill={MARKER} stroke="#ffffff" strokeWidth={1.5} />
  ) : null;

const LastMonthTouchpoints = ({ row, today }) => {
  const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  return (
    <div className="mt-2 border-t border-dashed border-zinc-700 pt-2">
      <div className="flex items-center gap-1.5">
        <span className="size-2 rounded-full" style={{ backgroundColor: MARKER }} />
        <span className="font-medium text-violet-300">
          {row.lastMonthTouchpoints} payment request{row.lastMonthTouchpoints === 1 ? "" : "s"} sent
        </span>
        <span className="rounded border border-zinc-700 px-1 text-[10px] uppercase tracking-wide text-zinc-400">
          Last month
        </span>
      </div>
      <p className="mt-0.5 text-zinc-400">
        {row.lastMonthFollowed > 0
          ? `${formatAmount(row.lastMonthFollowed)} cleared within ${FOLLOW_UP_DAYS} days in ${burndownMonthName(lastMonth)}`
          : `Nothing cleared within ${FOLLOW_UP_DAYS} days in ${burndownMonthName(lastMonth)}`}
      </p>
    </div>
  );
};

const ComparedToLastMonth = ({ row, today }) => {
  const change = row.total - row.lastMonth;
  if (change === 0) return null;
  const lastMonthLabel = `${row.day} ${shortMonthName(new Date(today.getFullYear(), today.getMonth() - 1, 1))}`;
  return (
    <div className="mt-2">
      <p className={cn("font-medium", change < 0 ? "text-green-400" : "text-red-400")}>
        {change < 0 ? "▼" : "▲"} {formatAmount(Math.abs(change))} ({percent(Math.abs(change), row.lastMonth)}){" "}
        {change < 0 ? "lower" : "higher"}
      </p>
      <p className="text-zinc-500">than {lastMonthLabel}, same point last month</p>
    </div>
  );
};

const MovementLine = ({ sign, label, amount, strong }) => (
  <div className={cn("flex justify-between gap-4", strong ? "font-semibold text-white" : "text-zinc-400")}>
    <dt>
      {sign && <span className="inline-block w-3">{sign}</span>}
      {label}
    </dt>
    <dd className="tabular-nums">{formatAmount(amount)}</dd>
  </div>
);

// How the day's close was reached from the day before. The parts are worked out per invoice, so they always
// sum to the close; "cleared" covers payments, credit notes and write-offs alike.
const DayMovement = ({ movement, closing }) => (
  <dl className="mt-2 space-y-0.5 border-t border-zinc-700 pt-2">
    <MovementLine label="Opening" amount={movement.opening} />
    {movement.fellDue > 0 && <MovementLine sign="+" label="Fell due" amount={movement.fellDue} />}
    {movement.cleared > 0 && <MovementLine sign="−" label="Cleared" amount={movement.cleared} />}
    {movement.otherIncrease > 0 && <MovementLine sign="+" label="Other increases" amount={movement.otherIncrease} />}
    <MovementLine sign="=" label="Closing" amount={closing} strong />
  </dl>
);

const BurndownTooltip = ({ active, payload, layers, today }) => {
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;
  const date = new Date(today.getFullYear(), today.getMonth(), row.day);
  return (
    <div className="w-60 rounded-lg bg-zinc-900 px-3 py-2.5 text-xs text-zinc-300 shadow-lg">
      <div className="flex items-baseline justify-between">
        <span className="font-semibold text-white">
          {row.day} {shortMonthName(date)}
        </span>
        <span className="text-zinc-500">{WEEKDAYS[date.getDay()]}</span>
      </div>
      {row.total !== null ? (
        <>
          <p className="mt-1 text-lg font-semibold leading-tight text-white">{formatAmount(row.total)}</p>
          <p className="text-zinc-500">overdue</p>
          <ul className="mt-2 space-y-0.5">
            {layers.map((layer) =>
              row[layer.key] > 0 || layer.index > 0 ? (
                <li key={layer.key} className="flex items-center gap-2">
                  <span className="size-2 rounded-sm" style={{ backgroundColor: STRONG[layer.index] }} />
                  <span className="flex-1">{layer.label}</span>
                  <span className="tabular-nums text-white">{formatAmount(row[layer.key])}</span>
                  <span className="w-8 text-right tabular-nums text-zinc-500">{percent(row[layer.key], row.total)}</span>
                </li>
              ) : null,
            )}
          </ul>
          {row.movement && <DayMovement movement={row.movement} closing={row.total} />}
          {row.collected > 0 && <p className="mt-1 text-zinc-500">{formatAmount(row.collected)} received in payments</p>}
          {row.lastMonth !== null && <ComparedToLastMonth row={row} today={today} />}
          {hasTouchpointMarker(row) && (
            <p className="mt-1 text-violet-300">
              {row.touchpoints} payment request{row.touchpoints === 1 ? "" : "s"} sent
            </p>
          )}
        </>
      ) : (
        <p className="mt-1 text-zinc-500">{row.future ? "Still to come" : "No data"}</p>
      )}
      {hasLastMonthMarker(row) && <LastMonthTouchpoints row={row} today={today} />}
    </div>
  );
};

const Legend = ({ layers }) => (
  <ul className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-zinc-500 lg:justify-end">
    {layers.map((layer) => (
      <li key={layer.key} className="flex items-center gap-1.5">
        <span className="size-2.5 rounded-sm ring-1 ring-inset ring-black/5" style={{ backgroundColor: GRAY[layer.index] }} />
        {layer.label}
      </li>
    ))}
    <li className="flex items-center gap-1.5">
      <span className="h-0.5 w-4 rounded-full" style={{ backgroundColor: LAST_MONTH }} />
      Same day last month
    </li>
    <li className="flex items-center gap-1.5">
      <svg viewBox="0 0 10 7" className="h-2 w-2.5" aria-hidden="true">
        <path d="M0,0 h10 l-5,7 z" fill={MARKER} />
      </svg>
      Ontik touchpoints
    </li>
  </ul>
);

const PickerOption = ({ icon, label, selected, onClick }) => (
  <MenuItem>
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-zinc-700 data-[focus]:bg-zinc-100"
    >
      {icon}
      <span className="flex-1 truncate">{label}</span>
      {selected && <CheckIcon aria-hidden="true" className="size-4 text-zinc-700" />}
    </button>
  </MenuItem>
);

const AllAccountsIcon = ({ className }) => (
  <span className={cn("flex shrink-0 items-center justify-center rounded-full bg-zinc-100 text-zinc-600", className)}>
    <UsersIcon aria-hidden="true" className="size-3" />
  </span>
);

// Narrows the chart to one working set; with none applied it charts all accounts. Shown only while the section is
// hovered or the menu is open.
const WorkingSetPicker = ({ sets, applied, onApply }) => (
  <Menu>
    {({ open }) => (
      <div
        className={cn(
          "transition-opacity has-[[data-focus]]:opacity-100 group-hover/burndown:opacity-100",
          open ? "opacity-100" : "opacity-0",
        )}
      >
        <MenuButton className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-white py-1 pl-1 pr-2 text-[13px] font-medium text-zinc-800 shadow-sm hover:border-zinc-300 data-[open]:border-zinc-300">
          {applied ? (
            <WorkingSetIcon icon={applied.icon} colour={applied.colour} className="size-5 rounded-full" iconClassName="size-3" />
          ) : (
            <AllAccountsIcon className="size-5" />
          )}
          <span className="max-w-40 truncate">{applied ? applied.name : "All accounts"}</span>
          <ChevronDownIcon aria-hidden="true" className="size-4 text-zinc-400" />
        </MenuButton>
        <MenuItems
          anchor="bottom end"
          className="z-30 mt-1 max-h-72 w-60 overflow-y-auto rounded-lg bg-white p-1 text-[13px] shadow-lg ring-1 ring-black/5 focus:outline-none"
        >
          <PickerOption
            icon={<AllAccountsIcon className="size-5" />}
            label="All accounts"
            selected={!applied}
            onClick={() => onApply(null)}
          />
          {sets.map((set) => (
            <PickerOption
              key={set.id}
              icon={<WorkingSetIcon icon={set.icon} colour={set.colour} className="size-5 rounded-full" iconClassName="size-3" />}
              label={set.name}
              selected={applied?.id === set.id}
              onClick={() => onApply(set.id)}
            />
          ))}
        </MenuItems>
      </div>
    )}
  </Menu>
);

// Axis labels at weekly steps plus today and month end, dropping any that would crowd today's label.
const axisTicks = (rows, todayDay) => {
  const last = rows.length;
  const candidates = [1, 7, 14, 21, 28]
    .filter((d) => last - d > 2)
    .concat(last)
    .filter((d) => Math.abs(d - todayDay) > 2);
  return [...new Set([...candidates, todayDay])].sort((a, b) => a - b);
};

const OverdueBurndownCard = ({ workingSets }) => {
  const today = useMemo(() => new Date(), []);
  const [setId, setSetId] = useState(null);
  const [hover, setHover] = useState(null);
  const hoveredDay = hover?.day ?? null;
  const applied = workingSets.find((s) => s.id === setId) ?? null;
  const { data, isLoading, isError } = useOverdueBurndown(applied?.filters ?? [], today);

  const month = burndownMonthName(today);
  const shortMonth = month.slice(0, 3);
  const layers = useMemo(() => burndownLayers(today).map((layer, index) => ({ ...layer, index })), [today]);
  const { rows, latest, change } = useMemo(() => buildBurndown(data?.days, today), [data, today]);
  const todayDay = today.getDate();
  const chartRows = rows.map((r) => ({ ...r, placeholder: r.future && latest ? latest : null }));
  const hasData = latest !== null;
  // Debt due this month is only overdue once its day passes, so its layer is often empty early on.
  const legendLayers = layers.filter((layer) => layer.index > 0 || rows.some((r) => r[layer.key] > 0));
  const barRamp = (row) =>
    row.day === todayDay || row.day === hoveredDay ? STRONG : hasTouchpointMarker(row) ? VIOLET : GRAY;

  return (
    <section className="group/burndown flex flex-col gap-4">
      <div className="flex h-7 items-center justify-between gap-4">
        <h3 className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">Overdue burndown</h3>
        {workingSets.length > 0 && <WorkingSetPicker sets={workingSets} applied={applied} onApply={setSetId} />}
      </div>

      <div className="flex flex-col gap-5 rounded-2xl border border-zinc-200 bg-white px-6 pb-4 pt-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between lg:gap-8">
          <div className="flex shrink-0 items-baseline gap-2.5 whitespace-nowrap">
            <span className="text-lg font-semibold tabular-nums text-zinc-900">
              {isLoading ? <span className="text-zinc-300">—</span> : hasData ? formatAmount(latest) : "—"}
            </span>
            {change !== null && change !== 0 && (
              <span className={cn("text-[13px] font-medium", change < 0 ? "text-green-700" : "text-red-700")}>
                {change < 0 ? "▼" : "▲"} {wholePounds(Math.abs(change))} vs last month
              </span>
            )}
          </div>
          <Legend layers={legendLayers} />
        </div>

        {isError ? (
          <p className="py-16 text-center text-sm text-zinc-400">Couldn’t load the burndown. Try again shortly.</p>
        ) : !isLoading && !hasData ? (
          <p className="py-16 text-center text-sm text-zinc-400">No overdue history for these accounts yet.</p>
        ) : (
          <div className="h-56 w-full" aria-label={`Overdue balance per day in ${month}`} role="img">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartRows} margin={{ top: 14, right: 4, bottom: 0, left: 0 }} barCategoryGap="18%"
                onMouseMove={(state) =>
                  setHover(state?.activeLabel != null ? { day: state.activeLabel, x: state.chartX, y: state.chartY } : null)
                }
                onMouseLeave={() => setHover(null)}>
                <CartesianGrid vertical={false} stroke="#f4f4f5" />
                <XAxis
                  dataKey="day"
                  ticks={axisTicks(rows, todayDay)}
                  interval={0}
                  tickLine={false}
                  axisLine={false}
                  tick={({ x, y, payload }) => (
                    <text x={x} y={y + 12} textAnchor="middle" fontSize={11}
                      fill={payload.value === todayDay ? "#18181b" : "#71717a"}
                      fontWeight={payload.value === todayDay ? 600 : 400}>
                      {payload.value === todayDay ? "Today" : `${payload.value} ${shortMonth}`}
                    </text>
                  )}
                />
                <YAxis tickFormatter={compactAmount} tickLine={false} axisLine={false} width={44} tickCount={5}
                  tick={{ fontSize: 11, fill: "#a1a1aa" }} />
                <Tooltip
                  cursor={false}
                  position={hover ? { x: hover.x + TOOLTIP_OFFSET, y: hover.y + TOOLTIP_OFFSET } : undefined}
                  allowEscapeViewBox={{ x: true, y: true }}
                  isAnimationActive={false}
                  wrapperStyle={{ zIndex: 20, pointerEvents: "none" }}
                  content={<BurndownTooltip layers={legendLayers} today={today} />}
                />
                {rows.filter(hasTouchpointMarker).map((r) => (
                  <ReferenceLine key={r.day} x={r.day} stroke={MARKER} strokeDasharray="3 3" strokeOpacity={0.6}
                    label={<TouchpointMarker />} ifOverflow="visible" />
                ))}
                {Array.from({ length: LAYER_COUNT }, (_, i) => (
                  <Bar key={`m${i}`} dataKey={`m${i}`} stackId="overdue" isAnimationActive={false}
                    stroke="#ffffff" strokeWidth={1}>
                    {chartRows.map((r) => (
                      <Cell key={r.day} fill={barRamp(r)[i]} />
                    ))}
                  </Bar>
                ))}
                <Bar dataKey="placeholder" stackId="overdue" shape={<PlaceholderBar />} isAnimationActive={false} />
                <Line dataKey="lastMonth" type="monotone" stroke={LAST_MONTH} strokeWidth={1.5} dot={<LastMonthTouchpointDot />}
                  activeDot={false} connectNulls isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </section>
  );
};

export default OverdueBurndownCard;
